import { ADDON_MODULE_KEYS, type AddonModuleKey, type ModuleKey } from './module-keys'

/* ==========================================================================
   Registro dei moduli
   --------------------------------------------------------------------------
   Una dashboard modulare ha bisogno di un posto solo in cui è scritto quali
   moduli esistono, come si chiamano, dove vivono e quanto costano. Da qui si
   generano la navigazione, le rotte e il pannello di attivazione: aggiungere
   un modulo è aggiungere una riga, non toccare cinque file.
   ========================================================================== */

/** Dove compare il modulo nella navigazione. */
export type ModuleGroup = 'lavoro' | 'moduli' | 'account'

export interface ModuleAffect {
  /** Il modulo in cui questa capacità si manifesta. */
  module: ModuleKey
  /** Cosa compare lì dentro, detto a chi lo userà. */
  what: string
}

export interface ModuleDefinition {
  key: ModuleKey
  /** Nome nella navigazione. Breve, perché il rail è stretto. */
  name: string
  /** Una riga: cosa fa per chi lo usa, non come è costruito. */
  summary: string
  path: string
  group: ModuleGroup
  /** Prezzo mensile in euro. Solo per i moduli opzionali. */
  price?: number
  /** Dove il modulo lascia un segno dentro altri moduli. */
  affects?: ModuleAffect[]
  /** Cosa succede ai contenuti quando si disattiva. */
  onDeactivate?: string
}

export const MODULE_REGISTRY: ModuleDefinition[] = [
  {
    key: 'ricerca',
    name: 'Ricerca',
    summary: 'Trova elementi per testo o partendo da una foto ritagliata.',
    path: '/ricerca',
    group: 'lavoro',
  },
  {
    key: 'collection',
    name: 'Collection',
    summary: 'Raccogli gli elementi scelti nelle storie che porterai al cliente.',
    path: '/collection',
    group: 'lavoro',
  },
  {
    key: 'presentazione',
    name: 'Presentazione',
    summary: 'Trasforma una collection in un documento condivisibile o in una scena 3D.',
    path: '/presentazione',
    group: 'lavoro',
  },

  {
    key: 'analitica',
    name: 'Analitica',
    summary: 'Come lavori tu e il tuo team: sessioni, moduli, tempi di scelta.',
    path: '/analitica',
    group: 'moduli',
    price: 39,
    onDeactivate: 'Le misurazioni continuano a essere raccolte; smetti solo di vederle.',
  },
  {
    key: 'trends',
    name: 'Trends',
    summary: 'Cosa si muove nel settore, a partire da un tema, un URL o un testo.',
    path: '/trends',
    group: 'moduli',
    price: 59,
    onDeactivate: 'Le ricerche già fatte restano nello storico.',
  },
  {
    key: 'recommender',
    name: 'Recommender',
    summary: 'Propone elementi dentro ogni collection e misura quanto ci prende.',
    path: '/recommender',
    group: 'moduli',
    price: 49,
    affects: [{ module: 'collection', what: 'la fascia dei suggerimenti sotto ogni collection' }],
    onDeactivate: 'Gli elementi già accettati restano nelle collection.',
  },
  {
    key: 'onirica',
    name: 'Onirica',
    summary: 'Fai domande alla dashboard a parole tue e ricevi risposte sui tuoi dati.',
    path: '/onirica',
    group: 'moduli',
    price: 79,
    affects: [
      { module: 'collection', what: 'il contrassegno sulle collection che ha citato' },
      { module: 'presentazione', what: 'il contrassegno sui documenti che ha citato' },
    ],
    onDeactivate: 'Le conversazioni restano archiviate.',
  },
  {
    key: 'generativa',
    name: 'Generativa',
    summary: 'Genera nuovi elementi da una collection e da un testo di supporto.',
    path: '/generativa',
    group: 'moduli',
    price: 99,
    affects: [{ module: 'ricerca', what: 'gli elementi generati, sempre etichettati come tali' }],
    onDeactivate: 'Gli elementi portati in archivio restano cercabili.',
  },

  {
    key: 'utilizzo',
    name: 'Utilizzo',
    summary: 'Quanti crediti hai speso, su cosa e con quale modulo.',
    path: '/utilizzo',
    group: 'account',
  },
  {
    key: 'impostazioni',
    name: 'Impostazioni',
    summary: 'Profilo, notifiche, moduli attivi, lingua.',
    path: '/impostazioni',
    group: 'account',
  },
  {
    key: 'amministratore',
    name: 'Amministratore',
    summary: 'Persone, gruppi, ruoli e chi può fare cosa su quale cliente.',
    path: '/amministratore',
    group: 'account',
  },
  {
    key: 'aziende',
    name: 'Aziende',
    summary:
      'Tutte le aziende clienti: piano, ruolo, ultimo accesso. Riservato agli amministratori.',
    path: '/aziende',
    group: 'account',
  },
]

const BY_KEY = new Map(MODULE_REGISTRY.map((module) => [module.key, module]))

export function getModule(key: ModuleKey): ModuleDefinition {
  const module = BY_KEY.get(key)
  if (!module) throw new Error(`Modulo sconosciuto: ${key}`)
  return module
}

/** I moduli opzionali, nell'ordine in cui compaiono nel pannello di attivazione. */
export const ADDON_MODULES: ModuleDefinition[] = ADDON_MODULE_KEYS.map((key) => getModule(key))

export function isAddonKey(key: ModuleKey): key is AddonModuleKey {
  return (ADDON_MODULE_KEYS as readonly ModuleKey[]).includes(key)
}

export function modulesInGroup(group: ModuleGroup): ModuleDefinition[] {
  return MODULE_REGISTRY.filter((module) => module.group === group)
}

/** Le etichette dei gruppi nel rail. Assenti per il gruppo di lavoro: è il default. */
export const GROUP_LABELS: Record<ModuleGroup, string | null> = {
  lavoro: null,
  moduli: 'Moduli',
  account: 'Account',
}
